<template>
  <el-dialog
    :title="$t('editOwnerRepair.title')"
    :visible.sync="visible"
    width="650px"
    custom-class="repair-design-dialog owner-repair-dialog"
    @close="resetForm"
  >
    <el-form ref="form" :model="formData" :rules="rules" label-width="86px" class="repair-design-form">
      <el-form-item :label="$t('addOwnerRepair.community')" prop="communityId">
        <el-select v-model="formData.communityId" :placeholder="$t('addOwnerRepair.selectPlaceholder')" disabled>
          <el-option :label="communityName" :value="formData.communityId" />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.repairArea')" prop="repairObjType">
        <el-radio-group v-model="formData.repairObjType" class="repair-area-radio" disabled>
          <el-radio label="001">{{ $t('addOwnerRepair.publicArea') }}</el-radio>
          <el-radio label="004">{{ $t('addOwnerRepair.room') }}</el-radio>
        </el-radio-group>
      </el-form-item>


      <el-form-item :label="$t('addOwnerRepair.repairType')" prop="repairSettingType">
        <el-select v-model="formData.repairSettingType" :placeholder="$t('addOwnerRepair.selectPlaceholder')" @change="handleRepairSettingTypeChange">
          <el-option :label="$t('repairSetting.cleaningOrder')" value="100" />
          <el-option :label="$t('repairSetting.repairOrder')" value="200" />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.typeName')" prop="repairType">
        <el-select v-model="formData.repairType" :placeholder="$t('addOwnerRepair.selectPlaceholder')">
          <el-option v-for="item in filteredRepairSettings" :key="item.repairType" :label="item.repairTypeName" :value="item.repairType" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="formData.repairObjType === '004'" :label="$t('addOwnerRepair.room')" prop="repairObjName">
        <el-input v-model="formData.repairObjName" disabled />
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.repairName')" prop="repairName">
        <el-input v-model="formData.repairName" :placeholder="$t('addOwnerRepair.inputPlaceholder')" />
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.tel')" prop="tel">
        <el-input v-model="formData.tel" type="tel" :placeholder="$t('addOwnerRepair.inputPlaceholder')" />
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.appointmentTime')" required>
        <div class="appointment-slot-line">
          <el-form-item prop="appointmentDate">
            <el-date-picker
              v-model="formData.appointmentDate"
              type="date"
              value-format="yyyy-MM-dd"
              :placeholder="$t('addOwnerRepair.placeholder.appointmentDate')"
              @change="handleAppointmentSlotChange"
            />
          </el-form-item>
          <el-form-item prop="appointmentSlot">
            <el-select v-model="formData.appointmentSlot" :placeholder="$t('addOwnerRepair.placeholder.appointmentSlot')" @change="handleAppointmentSlotChange">
              <el-option v-for="slot in appointmentSlots" :key="slot.value" :label="slot.label" :value="slot.value" />
            </el-select>
          </el-form-item>
        </div>
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.context')" prop="context">
        <el-input v-model="formData.context" :placeholder="$t('addOwnerRepair.inputPlaceholder')" />
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" @click="submitForm">{{ $t('common.confirm') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { updateOwnerRepair, listRepairSettings } from '@/api/work/ownerRepairManageApi'
import { getCommunityId, getCommunityName } from '@/api/community/communityApi'

export default {
  name: 'EditOwnerRepair',
  data() {
    return {
      visible: false,
      communityName: getCommunityName(),
      formData: this.getDefaultFormData(),
      repairSettings: [],
      appointmentSlots: Array.from({ length: 12 }, (_, index) => {
        const startHour = index * 2
        const endHour = startHour + 2
        return {
          value: String(startHour),
          label: `${String(startHour).padStart(2, '0')}:00-${endHour === 24 ? '24:00' : `${String(endHour).padStart(2, '0')}:00`}`
        }
      }),
      rules: {
        communityId: [{ required: true, message: this.$t('addOwnerRepair.placeholder.community'), trigger: 'change' }],
        repairObjType: [{ required: true, message: this.$t('addOwnerRepair.placeholder.repairArea'), trigger: 'change' }],
        repairSettingType: [{ required: true, message: this.$t('addOwnerRepair.placeholder.repairType'), trigger: 'change' }],
        repairType: [{ required: true, message: this.$t('addOwnerRepair.placeholder.typeName'), trigger: 'change' }],
        repairName: [{ required: true, message: this.$t('addOwnerRepair.placeholder.repairName'), trigger: 'blur' }],
        tel: [
          { required: true, message: this.$t('addOwnerRepair.placeholder.tel'), trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: this.$t('repairPoolManage.contactFormat'), trigger: 'blur' }
        ],
        appointmentDate: [{ required: true, message: this.$t('addOwnerRepair.placeholder.appointmentDate'), trigger: 'change' }],
        appointmentSlot: [{ required: true, message: this.$t('addOwnerRepair.placeholder.appointmentSlot'), trigger: 'change' }],
        context: [{ required: true, message: this.$t('addOwnerRepair.placeholder.context'), trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredRepairSettings() {
      if (!this.formData.repairSettingType) {
        return this.repairSettings
      }
      return this.repairSettings.filter(item => String(item.repairSettingType || '') === String(this.formData.repairSettingType))
    }
  },
  methods: {
    getDefaultFormData() {
      return {
        repairId: '',
        communityId: getCommunityId(),
        repairType: '',
        repairSettingType: '',
        repairName: '',
        tel: '',
        appointmentTime: '',
        appointmentStartTime: '',
        appointmentEndTime: '',
        appointmentDate: '',
        appointmentSlot: '',
        context: '',
        repairObjType: '',
        repairObjId: '',
        repairObjName: ''
      }
    },

    open(repair) {
      this.communityName = repair.communityName || getCommunityName()
      this.formData = {
        ...this.getDefaultFormData(),
        ...repair,
        communityId: repair.communityId || getCommunityId()
      }
      this.fillAppointmentSlot()
      this.listRepairSettings()
      this.visible = true
    },

    async listRepairSettings() {
      try {
        const params = {
          page: 1,
          row: 50,
          communityId: this.formData.communityId
        }
        const res = await listRepairSettings(params)
        this.repairSettings = res.data || []
        if (!this.formData.repairSettingType) {
          const current = this.repairSettings.find(item => item.repairType === this.formData.repairType)
          this.formData.repairSettingType = current ? current.repairSettingType : ''
        }
      } catch (error) {
        console.error('Failed to fetch repair settings:', error)
      }
    },

    handleRepairSettingTypeChange() {
      this.formData.repairType = ''
    },

    fillAppointmentSlot() {
      const startTime = this.formData.appointmentStartTime || this.formData.appointmentTime || ''
      if (!startTime) {
        this.formData.appointmentDate = ''
        this.formData.appointmentSlot = ''
        return
      }
      const match = String(startTime).match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}):/)
      if (!match) {
        this.formData.appointmentDate = ''
        this.formData.appointmentSlot = ''
        return
      }
      const hour = Number(match[2])
      const slotHour = Math.floor(hour / 2) * 2
      this.formData.appointmentDate = match[1]
      this.formData.appointmentSlot = String(slotHour)
      this.handleAppointmentSlotChange()
    },

    handleAppointmentSlotChange() {
      if (!this.formData.appointmentDate || this.formData.appointmentSlot === '') {
        this.formData.appointmentStartTime = ''
        this.formData.appointmentEndTime = ''
        this.formData.appointmentTime = ''
        return
      }
      const startHour = Number(this.formData.appointmentSlot)
      const endHour = startHour + 2
      this.formData.appointmentStartTime = `${this.formData.appointmentDate} ${this.formatHour(startHour)}:00:00`
      this.formData.appointmentEndTime = `${this.formData.appointmentDate} ${endHour === 24 ? '23:59:59' : `${this.formatHour(endHour)}:00:00`}`
      this.formData.appointmentTime = this.formData.appointmentStartTime
    },

    formatHour(hour) {
      return String(hour).padStart(2, '0')
    },

    async submitForm() {
      this.handleAppointmentSlotChange()
      this.$refs.form.validate(async valid => {
        if (valid) {
          try {
            if (this.formData.repairObjType === '001') {
              this.formData.repairObjId = this.formData.communityId
              this.formData.repairObjName = this.communityName
            }
            await updateOwnerRepair({
              ...this.formData,
              communityId: this.formData.communityId
            })
            this.$message.success(this.$t('common.operationSuccess'))
            this.visible = false
            this.$emit('success')
          } catch (error) {
            console.error('Update owner repair failed:', error)
          }
        }
      })
    },

    resetForm() {
      this.$refs.form && this.$refs.form.resetFields()
      this.formData = this.getDefaultFormData()
    }
  }
}
</script>

<style lang="scss">
.repair-design-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    height: 66px;
    padding: 0 24px;
    background: #ebf2fa;
    border-bottom: 1px solid #dce6f5;
    line-height: 66px;
  }

  .el-dialog__title {
    color: #1d2129;
    font-size: 16px;
    font-weight: 600;
  }

  .el-dialog__headerbtn {
    top: 23px;
    right: 24px;
  }

  .el-dialog__body {
    padding: 24px 24px 14px;
  }

  .el-dialog__footer {
    padding: 10px 24px 24px;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .el-button {
    min-width: 98px;
    height: 38px;
    border-radius: 10px;
    font-size: 14px;
  }

  .el-button--default {
    color: #0e76ff;
    border-color: #0e76ff;
  }

  .el-button--primary {
    background: #0e76ff;
    border-color: #0e76ff;
  }
}

.repair-design-form {
  .el-form-item {
    margin-bottom: 18px;
  }

  .el-form-item__label {
    color: #1d2129;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  .el-form-item__content {
    line-height: 38px;
  }

  .el-input__inner,
  .el-textarea__inner {
    height: 38px;
    border-color: #dce6f5;
    border-radius: 10px;
    color: #1d2129;
    font-size: 14px;
  }

  .el-select,
  .el-date-editor.el-input {
    width: 100%;
  }

  .repair-area-radio {
    height: 38px;
    display: flex;
    align-items: center;
  }

  .appointment-slot-line {
    display: flex;
    gap: 12px;

    .el-form-item {
      flex: 1;
      margin-bottom: 0;
    }
  }
}
</style>
